<template>
  <div id="CancellationPolicy" class="container-fluid px-0">
    <div class="main">
      <div class="page-header">
        <div class="page-title d-flex flex-column align-items-center">
          <img class="logo" src="/img/spectrum_life_logo_light.png" />
          <h2 class="brand-text mb-0">
            Cancellation and Reschedule Policy for Services
          </h2>
        </div>
      </div>
      <div class="content">
        <div class="content-card">
          <h3>1. Overview & Definitions</h3>
          <p>
            This cancellation policy applies to all self-pay clinical and
            wellbeing services, including coaching sessions, neurodiversity
            services, open-ended digital therapy, and single-session
            consultations. It aims to ensure fairness and clarity for both users
            and service providers.
          </p>
          <p>
            <b>"User" or "You"</b> – Refers to any individual accessing or using
            the Services.
          </p>
          <p>
            <b>Supplier/Spectrum Life</b> - SPECTRUM Wellness UK Limited t/a
            Spectrum.Life, a company incorporated in England and Wales under
            number 11815511 whose registered office is at Huckletree, Ancoats, 9
            Great Ancoats St, Manchester, United Kingdom, M4 5AD
          </p>
          <h3>2. Cancellation/Reschedule Requirements</h3>
          <h4>2.1 Notice Period</h4>
          <ul>
            <li>
              Appointments must be cancelled or rescheduled at least
              <b>24 hours in advance</b> of the scheduled session.
            </li>
          </ul>
          <h4>2.2 How to Cancel</h4>
          <ul>
            <li>
              Appointments Over 24 Hours Away:
              <ul>
                <li>
                  Users can cancel or reschedule appointments through the
                  Simplyhealth platform if the appointment is more than 24 hours
                  away.
                </li>
              </ul>
            </li>
            <li>
              Appointments Within 24 Hours: If the appointment start time is in
              less than 24 hours, users can cancel or reschedule by contacting
              customer support directly via the email address provided below
              <ul>
                <li>
                  For queries regarding cancellations or rescheduling on
                  coaching services please email
                  <a href="mailto:coachingappointments@spectrum.life"
                    >coachingappointments@spectrum.life</a
                  >
                </li>
                <li>
                  For queries regarding cancellations or rescheduling on
                  neurodiversity and open-ended digital therapy services please
                  email
                  <a href="mailto:appointments@spectrum.life"
                    >appointments@spectrum.life</a
                  >
                </li>
              </ul>
            </li>
          </ul>
          <h3>3. Fees and Refunds</h3>
          <h4>
            3.1 Cancellations and Reschedules with Less than 24 Hours’ Notice
          </h4>
          <ul>
            <li>
              A cancellation fee of <b>25% of the session cost</b> will be
              charged. The remaining 75% will be refunded to the original
              payment method within 3-5 business days.
            </li>
          </ul>
          <h4>3.2 Missed Appointments or a No-Show</h4>
          <ul>
            <li>
              It is considered a Missed Appointment or a No Show if a user does
              not join their appointment in the first 5 minutes after their
              scheduled appointment time. In this instance, the Supplier will
              end the appointment and inform the user of their non-attendance.
            </li>
            <li>
              A <b>50% session cost</b> will be charged for missed appointments
              or No Shows, without prior notification. The remaining 50% will be
              refunded to the original payment method within 3-5 business days
            </li>
          </ul>
          <h4>3.3 Timely Cancellations and Reschedules</h4>
          <ul>
            <li>
              Appointments cancelled or rescheduled more than 24 hours in
              advance will receive a <b>full refund</b> to the original payment
              method within 3-5 business days
            </li>
          </ul>
          <h4>3.4 Missed or Rescheduled Appointments by Supplier</h4>
          <ul>
            <li>
              If the Supplier does not attend within the first 5 minutes of a
              scheduled appointment, the appointment may need to be cancelled,
              and the User can choose to receive either a full refund to the
              original payment method within 3-5 business days or a rescheduled
              appointment at no additional cost.
            </li>
            <li>
              If the Supplier initiates a reschedule, the User will not incur
              any rescheduling fees and may choose to proceed with the
              rescheduled appointment or receive a full refund to the original
              payment method within 3-5 business days.
            </li>
            <li>
              If the Supplier cancels an appointment, the User will not be
              responsible for any cancellation fees and may choose to receive
              either a full refund to the original payment method within 3-5
              business days or a rescheduled appointment at no additional cost
            </li>
            <li>
              It is the responsibility of the Supplier to contact the User if a
              facilitating practitioner becomes unavailable for the User’s
              appointment, or is running more than 5 minutes late, by contacting
              the User via the email and or phone number provided when
              registering for the appointment.
            </li>
          </ul>
          <p>
            <b>3.5</b> For queries regarding refunds on coaching services please
            email
            <a href="mailto:coachingappointments@spectrum.life"
              >coachingappointments@spectrum.life</a
            >
          </p>
          <p>
            For queries regarding refunds on neurodiversity and open-ended
            digital therapy services please email
            <a href="mailto:appointments@spectrum.life"
              >appointments@spectrum.life</a
            >
          </p>
          <h3>4. Exceptions</h3>
          <p>
            <b>4.1</b> Refunds or cancellation fees may be waived in cases of
            unforeseen emergencies or extenuating circumstances, subject at all
            times to the discretion of the Supplier to be reviewed on a
            case-by-case basis by Spectrum.Life
          </p>
          <p>
            <b>4.2</b> If engaging in the Mental Health or Sleep Coaching
            programmes of 3 sessions and the 2nd or 3rd (final) session is
            missed or cancelled under 24 hours, that session is considered lost
            and will not be refunded. Extenuating circumstances may apply.
          </p>
          <p>
            <b>4.3</b> In cases where the User’s needs exceed the scope of this
            service and so a consultation is deemed clinically inappropriate,
            the User will receive a full refund.
          </p>
          <h3>
            5. Parental Consent and Safeguarding (Applicable for CYP Services)
          </h3>
          <p>
            <b>5.1</b> Parental consent is mandatory for all bookings involving
            children and young persons (CYP). Any cancellations or rescheduling
            requests must be submitted by the parent or guardian.
          </p>
          <p>
            <b>5.2</b> For open-ended therapy and neurodiversity assessments, if
            the parent or guardian f fails to attend required sessions as
            clarified/requested by the clinician, Spectrum reserves the right to
            decline further bookings until compliance is achieved.
          </p>
          <h3>6. Disclaimers</h3>
          <p>
            <b>6.1</b> Refunds and cancellations do not apply to Services that
            have already been rendered.
          </p>
          <p>
            <b>6.2</b> Repeated last-minute cancellations or no-shows may result
            in the suspension of service access at all times at the discretion
            of the Spectrum Life.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CancellationPolicy"
};
</script>

<style lang="less">
#CancellationPolicy {
  .main {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .page-header {
    padding: 60px 20px 20px;
    background-color: #fbfafa;
  }

  .logo {
    max-width: 350px;
    width: 100%;
    padding-bottom: 50px;
  }

  .page-title {
    h2 {
      text-align: center;
      font-size: 3.8rem;
      letter-spacing: normal;
      font-family: "PlusJakartaSansBold";
    }
  }

  .content {
    flex: 1 1 100%;
    padding: 20px 20px 60px;
    background-color: #fbfafa;
  }

  .content-card {
    height: 100%;
    background: #ffffff;
    max-width: 1000px;
    padding: 40px 60px 30px;
    margin: 0 auto;
    border-radius: 16px;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: #212529;
      font-family: "PlusJakartaSansBold";
    }

    h3 {
      font-size: 2.2rem;
      letter-spacing: 0;
      padding: 32px 0 0;
      margin: 0 0 24px;
    }

    h4 {
      font-size: 1.6rem;
      letter-spacing: 0;
      padding: 0;
      margin: 0 0 4px;
    }

    p {
      color: #212529;
      padding: 0;
      margin: 0 0 16px;
      font-size: 1.6rem;
    }

    ul,
    ol {
      color: #212529;
      margin: 0 0 16px;
    }

    li {
      color: #212529;
      font-size: 1.6rem;
    }

    b {
      font-family: "PlusJakartaSansBold";
    }

    a {
      color: #3c16f9;

      &:hover,
      &:active {
        color: #3c16f9;
      }
    }
  }
  @media only screen and (max-width: 480px) {
    .content-card {
      padding: 30px 20px;
    }
  }
}
</style>
